import { getCurrentLocation } from '@/shared/integrations/google-places';
import { userStore } from '@/entities/user';
import { getApolloClient } from '@/shared/api/apollo-client';
import {
  GetAccessibleOrganizationListDocument,
  type GetAccessibleOrganizationListQuery,
  type GetAccessibleOrganizationListQueryVariables,
} from '@/entities/organization';
import { showError } from '@drisk-ai/ui';
import { redirect } from '@tanstack/react-router';
import {
  GetAccessibleClientGroupListDocument,
  type GetAccessibleClientGroupListQuery,
  type GetAccessibleClientGroupListQueryVariables,
} from '@/entities/client-group';
import { setAppDataStore } from '@/entities/app-data';

const client = getApolloClient();
const INIT_LIST_PAGE_SIZE = 100;

type InitAppDataStoreContext = {
  organizationUuid?: string;
  clientGroupUuid?: string;
};

async function initUserCurrentAddress() {
  getCurrentLocation()
    .then(userCurrentAddress => {
      setAppDataStore({ userCurrentAddress });
    })
    .catch(e => console.error('Failed to get current location:', e));
}

async function initInitialGlobalOrganizationAndClientGroup(
  context?: InitAppDataStoreContext,
) {
  let organization: { name: string; uuid: string } | null = null;
  let clientGroup: { name: string; uuid: string } | null = null;

  const currentUser = userStore.state;

  if (!currentUser) {
    throw new Error('Current user data is not initialized');
  }

  const { data: organizationData } = await client.query<
    GetAccessibleOrganizationListQuery,
    GetAccessibleOrganizationListQueryVariables
  >({
    query: GetAccessibleOrganizationListDocument,
    variables: {
      input: {
        pagination: {
          pageNumber: 1,
          pageSize: INIT_LIST_PAGE_SIZE,
        },
      },
    },
  });

  const organizations = organizationData.getAccessibleOrganizationList.items;

  if (!organizations.length) {
    showError('No accessible organization found for the user.');
    throw redirect({
      to: '/forbidden',
    });
  }

  if (context?.organizationUuid) {
    organization =
      organizations.find(item => item.uuid === context.organizationUuid) ??
      null;

    if (!organization) {
      showError('No accessible organization found for the joined event.');
      throw redirect({
        to: '/forbidden',
      });
    }
  } else if (currentUser.lastSelectedOrganization) {
    organization =
      organizations.find(
        item => item.uuid === currentUser.lastSelectedOrganization?.uuid,
      ) ?? currentUser.lastSelectedOrganization;
  } else {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    organization = organizations[0]!;
  }

  const { data: clientGroupData } = await client.query<
    GetAccessibleClientGroupListQuery,
    GetAccessibleClientGroupListQueryVariables
  >({
    query: GetAccessibleClientGroupListDocument,
    variables: {
      input: {
        pagination: {
          pageNumber: 1,
          pageSize: INIT_LIST_PAGE_SIZE,
        },
        organizationUuid: organization.uuid,
      },
    },
  });

  const clientGroups = clientGroupData.getAccessibleClientGroupList.items;

  if (!clientGroups.length) {
    showError('No accessible client group found for the user.');
    throw redirect({
      to: '/forbidden',
    });
  }

  if (context?.clientGroupUuid) {
    clientGroup =
      clientGroups.find(item => item.uuid === context.clientGroupUuid) ?? null;

    if (!clientGroup) {
      showError('No accessible client group found for the joined event.');
      throw redirect({
        to: '/forbidden',
      });
    }
  } else if (currentUser.lastSelectedClientGroup) {
    clientGroup =
      clientGroups.find(
        item => item.uuid === currentUser.lastSelectedClientGroup?.uuid,
      ) ?? null;
  }

  clientGroup ??= clientGroups[0];

  setAppDataStore({
    selectedClientGroup: clientGroup,
    selectedOrganization: organization,
  });
}

export async function initAppDataStore(context?: InitAppDataStoreContext) {
  await initUserCurrentAddress();
  await initInitialGlobalOrganizationAndClientGroup(context);
}
